import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LinkedIn } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { toJpeg } from 'html-to-image';
import NoDataOverlay from '../Insights/NoDataOverlay';
import CertificationBadge from './CertificationBadge';
import CertificationCard from './CertificationCard';
import {
  CERTIFICATION_DISPLAY_ORDER,
  getCertificationDisplayName
} from '../../constants/certificationBadgeCopy';
import {
  getCertificationScreenOverlayContent,
  isCertificationScreenBlocked
} from '../../constants/certificationScreenState';
import { sendRequest } from '../../utils/dateHelpers';
import { getCertificationLogoFromSource } from '../../utils/companyConfigurations';
import { normalizeCertificationBadgeUrls } from '../../utils/certificationLinks';

const API_CERTIFICATIONS_URL = '/certifications/company';
const API_COMPANY_CONFIGURATIONS_URL = '/company-configurations';
const waitForNextPaint = () =>
  new Promise(resolve => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });

const buildIframeCode = badge =>
  `<iframe src="${badge.embed_url}" title="${getCertificationDisplayName(
    badge
  )}" width="520" height="420" frameborder="0" loading="lazy" allowtransparency="true" style="background: transparent; border: 0;"></iframe>`;

const createPlaceholderBadge = badgeKey => ({
  badge_key: badgeKey,
  status: 'not_earned',
  display_name: getCertificationDisplayName({ badge_key: badgeKey })
});

const normalizeBadges = badges => {
  const incoming = new Map(
    (Array.isArray(badges) ? badges : []).map(badge => [
      badge?.badge_key,
      badge?.badge_key ? normalizeCertificationBadgeUrls(badge) : badge
    ])
  );

  return CERTIFICATION_DISPLAY_ORDER.map(
    badgeKey => incoming.get(badgeKey) || createPlaceholderBadge(badgeKey)
  );
};

const CertificationsPage = () => {
  const { userInfo = {} } = useSelector(state => state);
  const user = userInfo?.user || {};
  const previewCardRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [screenState, setScreenState] = useState('');
  const [screenStateMeta, setScreenStateMeta] = useState(null);
  const [latestEvaluatedEndYearMonth, setLatestEvaluatedEndYearMonth] =
    useState('');
  const [badges, setBadges] = useState([]);
  const [hasCertificationLogo, setHasCertificationLogo] = useState(false);
  const [selectedPreviewBadgeKey, setSelectedPreviewBadgeKey] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCertifications = async () => {
      try {
        setLoading(true);
        const [certificationsResponse, configurationsResponse] =
          await Promise.all([
            sendRequest(API_CERTIFICATIONS_URL, 'GET'),
            sendRequest(API_COMPANY_CONFIGURATIONS_URL, 'GET').catch(() => null)
          ]);

        if (!isMounted) {
          return;
        }

        setScreenState(certificationsResponse?.screen_state || '');
        setScreenStateMeta(certificationsResponse?.screen_state_meta || null);
        setLatestEvaluatedEndYearMonth(
          certificationsResponse?.latest_evaluated_end_year_month || ''
        );
        setBadges(normalizeBadges(certificationsResponse?.badges));
        setHasCertificationLogo(
          Boolean(getCertificationLogoFromSource(configurationsResponse))
        );
      } catch (error) {
        if (isMounted) {
          toast.error(error?.message || 'Unable to load certifications.');
          setBadges(normalizeBadges([]));
          setHasCertificationLogo(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCertifications();

    return () => {
      isMounted = false;
    };
  }, []);

  const overlayContent = getCertificationScreenOverlayContent(
    screenState,
    screenStateMeta
  );
  const isScreenBlocked = isCertificationScreenBlocked(screenState);

  const handleCopy = async (value, successMessage) => {
    if (!value) {
      toast.error('This item is not available yet.');
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch (error) {
      toast.error('Unable to copy this item.');
    }
  };

  const handleDownloadPreview = async badge => {
    if (badge?.status === 'not_earned') {
      toast.error('This certification is not available yet.');
      return;
    }

    setSelectedPreviewBadgeKey(badge?.badge_key || null);
    await waitForNextPaint();

    const previewElement = previewCardRef.current;
    if (!previewElement) {
      toast.error('Preview not found.');
      return;
    }

    try {
      toast.info('Generating preview...');
      window.getSelection?.()?.removeAllRanges?.();

      const dataUrl = await toJpeg(previewElement, {
        quality: 0.95,
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff'
      });

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `gitme-${badge.badge_key}-linkedin-preview.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Preview downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download preview.');
    }
  };

  const previewBadge = useMemo(() => {
    const selectedBadge = badges.find(
      item => item?.badge_key === selectedPreviewBadgeKey
    );

    if (selectedBadge) {
      return selectedBadge;
    }

    return (
      badges.find(item => item?.status === 'active') || {
        badge_key: 'delivers_with_impact',
        status: 'active',
        valid_through_end_year_month: latestEvaluatedEndYearMonth || '2026-05'
      }
    );
  }, [badges, latestEvaluatedEndYearMonth, selectedPreviewBadgeKey]);

  return (
    <section className="relative space-y-8 p-4 sm:p-6">
      {loading || hasCertificationLogo ? null : (
        <Link
          to="/settings/configurations"
          className="inline-flex w-fit items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 transition hover:bg-amber-100"
        >
          <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-[15px] font-semibold text-amber-700">
            *
          </span>
          <span className="leading-6">
            Add your company logo in Configurations so it appears on earned
            public certification previews.
          </span>
        </Link>
      )}

      <div
        id="certification-grid"
        className={`grid grid-cols-1 gap-6 xl:grid-cols-3 ${
          !loading && isScreenBlocked
            ? 'pointer-events-none select-none blur-sm'
            : ''
        }`}
      >
        {(loading ? Array.from({ length: 3 }) : badges).map((badge, index) => {
          if (loading) {
            return (
              <div
                key={`loading-${index}`}
                className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="h-80 animate-pulse rounded-[28px] bg-slate-100" />
                <div className="mt-5 h-6 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-36 animate-pulse rounded-2xl bg-slate-100" />
              </div>
            );
          }

          const companyName =
            user?.companyName || badge?.company_name || 'Your Company';

          return (
            <CertificationCard
              key={badge.badge_key}
              badge={badge}
              companyName={companyName}
              onCopyVerificationLink={() =>
                handleCopy(badge?.share_url, 'Verification link copied.')
              }
              onEmbedBadge={() =>
                handleCopy(
                  buildIframeCode(badge),
                  'Verified badge embed code copied.'
                )
              }
              onDownloadPreview={() => handleDownloadPreview(badge)}
            />
          );
        })}
      </div>

      <div>
        <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0A66C2] shadow-md">
              <LinkedIn sx={{ fontSize: 20, color: 'white' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-[-0.02em] text-slate-950">
                LinkedIn Share Preview
              </h2>
              <p className="text-[11px] leading-4 text-slate-500">
                1200x630px · Optimized
              </p>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto pb-2">
            <div className="w-max min-w-full">
              <div ref={previewCardRef} className="inline-block">
                <CertificationBadge
                  badge={previewBadge}
                  companyName={user?.companyName || 'Alex Morgan'}
                  variant="preview"
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      {!loading && overlayContent ? (
        <NoDataOverlay
          title={overlayContent.title}
          body={overlayContent.body}
          ctaLabel={overlayContent.ctaLabel}
          ctaPath={overlayContent.ctaPath}
        />
      ) : null}
    </section>
  );
};

export default CertificationsPage;
